import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Calendar, Star, Settings, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBookings: 0,
    upcomingCleanings: 0,
    featuredReviews: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [bookingsRes, cleaningRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/cleaning`),
        axios.get(`${API}/reviews?featured=true`)
      ]);
      
      setStats({
        totalBookings: bookingsRes.data.length,
        upcomingCleanings: cleaningRes.data.filter(c => c.status === 'scheduled').length,
        featuredReviews: reviewsRes.data.length
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <h1 className="text-3xl font-heading font-bold text-primary mb-8">
          Dashboard Overview
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Total Bookings
              </CardTitle>
              <BookOpen className="w-4 h-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary" data-testid="total-bookings">{stats.totalBookings}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Upcoming Cleanings
              </CardTitle>
              <Calendar className="w-4 h-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary" data-testid="upcoming-cleanings">{stats.upcomingCleanings}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                Featured Reviews
              </CardTitle>
              <Star className="w-4 h-4 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary" data-testid="featured-reviews">{stats.featuredReviews}</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/bookings')}
                data-testid="manage-bookings-button"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Manage Bookings
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/cleaning')}
                data-testid="manage-cleaning-button"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Manage Cleaning Schedule
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/reviews')}
                data-testid="manage-reviews-button"
              >
                <Star className="w-4 h-4 mr-2" />
                Manage Reviews
              </Button>
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={() => navigate('/admin/settings')}
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Getting Started</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2 text-slate-600">
                <li>Configure your API keys in Settings</li>
                <li>Add reviews from your Airbnb guests</li>
                <li>Set up your cleaning schedule</li>
                <li>Start receiving direct bookings!</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;